/**
 * StudentMyClassesPage
 *
 * Shows a student's confirmed class enrollments with full context:
 * teacher name, next session, session counts, and expandable summaries.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  BookOpen, CalendarDays, ChevronDown, ChevronRight, ChevronUp,
  Clock, GraduationCap, Search, Users, Zap, CalendarCheck,
  FileText, User,
} from "lucide-react";
import { StudentLayout } from "@/components/student/layout/StudentLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useMyClasses } from "@/hooks/use-class-management";
import { cn } from "@/lib/utils";
import type { StudentClassItem, SummaryItem } from "@/services/classManagementService";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
    + " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function offeringTypeLabel(type: string | undefined) {
  if (type === "ONE_TO_ONE") return "1-on-1";
  if (type === "GROUP") return "Group";
  return "Class";
}

function OfferingTypeIcon({ type }: { type: string | undefined }) {
  if (type === "ONE_TO_ONE") return <GraduationCap className="w-5 h-5 text-indigo-600" aria-hidden />;
  if (type === "GROUP") return <Users className="w-5 h-5 text-indigo-600" aria-hidden />;
  return <Zap className="w-5 h-5 text-indigo-600" aria-hidden />;
}

// ── Summary list ──────────────────────────────────────────────────────────────

function SummaryList({ summaries }: { summaries: SummaryItem[] }) {
  const sorted = [...summaries].sort(
    (a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
  );
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 2;
  const visible = expanded ? sorted : sorted.slice(0, PREVIEW);
  const extra = sorted.length - PREVIEW;

  return (
    <div className="space-y-2">
      {visible.map((summary) => (
        <div
          key={summary.sessionId}
          className="rounded-xl bg-slate-50 border border-slate-100 p-4"
        >
          <p className="text-xs text-slate-400 mb-2 flex items-center gap-1.5">
            <Clock className="w-3 h-3" aria-hidden />
            {formatDate(summary.sentAt)}
          </p>
          <p className="text-sm text-slate-700 leading-relaxed">{summary.body}</p>
        </div>
      ))}
      {extra > 0 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors cursor-pointer"
        >
          {expanded
            ? <><ChevronUp className="w-3.5 h-3.5" /> Show less</>
            : <><ChevronDown className="w-3.5 h-3.5" /> Show {extra} more {extra === 1 ? "summary" : "summaries"}</>
          }
        </button>
      )}
    </div>
  );
}

// ── Enrollment card ───────────────────────────────────────────────────────────

function EnrollmentCard({ enrollment }: { enrollment: StudentClassItem }) {
  const [summariesOpen, setSummariesOpen] = useState(false);
  const hasSummaries = enrollment.summaries.length > 0;

  return (
    <article
      aria-label={enrollment.title}
      className="bg-white rounded-2xl border border-slate-200 hover:border-indigo-200 hover:shadow-sm transition-all duration-200 overflow-hidden"
    >

      {/* Top colour strip */}
      <div className="h-1 bg-gradient-to-r from-indigo-500 to-indigo-400" />

      <div className="p-5 space-y-4">
        {/* Header row */}
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
            <OfferingTypeIcon type={enrollment.offeringType} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900 leading-snug">
                {enrollment.title || "Untitled class"}
              </h2>
              <Badge className="shrink-0 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-50">
                {offeringTypeLabel(enrollment.offeringType)}
              </Badge>
            </div>
            {enrollment.teacherName && (
              <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 shrink-0" aria-hidden />
                {enrollment.teacherName}
              </p>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {/* Next session */}
          <div className="bg-slate-50 rounded-xl p-3 col-span-2 sm:col-span-1">
            <p className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
              <CalendarCheck className="w-3.5 h-3.5" aria-hidden /> Next session
            </p>
            {enrollment.nextSessionAt ? (
              <p className="text-sm font-semibold text-slate-800">
                {formatDateTime(enrollment.nextSessionAt)}
              </p>
            ) : (
              <p className="text-sm text-slate-400">Not scheduled</p>
            )}
          </div>

          {/* Sessions progress */}
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
              <CalendarDays className="w-3.5 h-3.5" aria-hidden /> Sessions
            </p>
            {(() => {
              const done = enrollment.totalSessions - enrollment.upcomingSessions;
              return (
                <p className="text-sm font-semibold text-slate-800">
                  <span className="text-emerald-700">{done} done</span>
                  {enrollment.upcomingSessions > 0 && (
                    <span className="ml-1.5 text-xs font-medium text-indigo-600">
                      · {enrollment.upcomingSessions} upcoming
                    </span>
                  )}
                </p>
              );
            })()}
          </div>

          {/* Enrolled since */}
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
              <BookOpen className="w-3.5 h-3.5" aria-hidden /> Enrolled
            </p>
            <p className="text-sm font-semibold text-slate-800">{formatDate(enrollment.addedAt)}</p>
          </div>
        </div>

        {/* Session summaries toggle */}
        <div className="border-t border-slate-100 pt-3">
          <button
            onClick={() => setSummariesOpen((v) => !v)}
            className={cn(
              "w-full flex items-center justify-between text-sm font-medium transition-colors duration-150 cursor-pointer",
              hasSummaries ? "text-slate-700 hover:text-indigo-700" : "text-slate-400 cursor-default",
            )}
            disabled={!hasSummaries}
          >
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4" aria-hidden />
              {hasSummaries
                ? `${enrollment.summaries.length} session ${enrollment.summaries.length === 1 ? "summary" : "summaries"}`
                : "No session summaries yet"
              }
            </span>
            {hasSummaries && (
              summariesOpen
                ? <ChevronUp className="w-4 h-4 text-slate-400" />
                : <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </button>

          {!hasSummaries && (
            <p className="text-xs text-slate-400 mt-1 pl-6">
              Your teacher will share summaries after each session.
            </p>
          )}

          {summariesOpen && hasSummaries && (
            <div className="mt-3">
              <SummaryList summaries={enrollment.summaries} />
            </div>
          )}
        </div>

        {/* View details link */}
        <div className="border-t border-slate-100 pt-3">
          <Link
            to={`/my-classes/${enrollment.offeringId}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
          >
            View sessions & messages
            <ChevronRight className="w-3.5 h-3.5" aria-hidden />
          </Link>
        </div>
      </div>
    </article>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div role="status" aria-label="Loading classes" className="space-y-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="h-1 bg-slate-100" />
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-3">
              <Skeleton className="w-11 h-11 rounded-xl shrink-0" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Skeleton className="h-16 rounded-xl col-span-1" />
              <Skeleton className="h-16 rounded-xl" />
              <Skeleton className="h-16 rounded-xl" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function StudentMyClassesPage() {
  usePageTitle("My Classes | BookMyCoaching");
  const { data, isLoading, isError } = useMyClasses();

  const confirmedClasses = (data ?? [])
    .filter((e) => e.status === "CONFIRMED")
    .sort((a, b) => {
      // Classes with an upcoming session come first
      if (a.nextSessionAt && !b.nextSessionAt) return -1;
      if (!a.nextSessionAt && b.nextSessionAt) return 1;
      if (a.nextSessionAt && b.nextSessionAt)
        return new Date(a.nextSessionAt).getTime() - new Date(b.nextSessionAt).getTime();
      return new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime();
    });

  const totalUpcoming = confirmedClasses.reduce((s, e) => s + e.upcomingSessions, 0);
  const totalSummaries = confirmedClasses.reduce((s, e) => s + e.summaries.length, 0);

  return (
    <StudentLayout>
      <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100 shrink-0 bg-white">
        <h1 className="text-2xl font-bold text-slate-900">My Classes</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Your enrolled classes, upcoming sessions, and summaries from your teachers
        </p>
      </div>

      <main className="flex-1 overflow-auto bg-slate-50">
        <div className="px-4 sm:px-6 py-6 max-w-3xl space-y-5">

          {/* Stats strip */}
          {!isLoading && !isError && confirmedClasses.length > 0 && (
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
                <BookOpen className="w-4 h-4 text-indigo-500" aria-hidden />
                <span className="text-sm font-semibold text-slate-800">{confirmedClasses.length}</span>
                <span className="text-sm text-slate-500">{confirmedClasses.length === 1 ? "class" : "classes"}</span>
              </div>
              {totalUpcoming > 0 && (
                <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-2.5 shadow-sm">
                  <CalendarCheck className="w-4 h-4 text-indigo-500" aria-hidden />
                  <span className="text-sm font-semibold text-indigo-800">{totalUpcoming}</span>
                  <span className="text-sm text-indigo-700">upcoming {totalUpcoming === 1 ? "session" : "sessions"}</span>
                </div>
              )}
              {totalSummaries > 0 && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 shadow-sm">
                  <FileText className="w-4 h-4 text-emerald-500" aria-hidden />
                  <span className="text-sm font-semibold text-emerald-800">{totalSummaries}</span>
                  <span className="text-sm text-emerald-700">{totalSummaries === 1 ? "summary" : "summaries"}</span>
                </div>
              )}
            </div>
          )}

          {isLoading && <PageSkeleton />}

          {isError && !isLoading && (
            <div className="flex flex-col items-center justify-center text-center py-16 bg-white rounded-2xl border border-slate-200">
              <BookOpen className="w-8 h-8 text-slate-300 mb-3" aria-hidden />
              <p className="text-sm font-semibold text-slate-700 mb-1">Could not load your classes</p>
              <p className="text-sm text-slate-500">Please refresh the page to try again.</p>
            </div>
          )}

          {!isLoading && !isError && confirmedClasses.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center py-16 bg-white rounded-2xl border border-slate-200">
              <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
                <BookOpen className="w-7 h-7 text-indigo-400" aria-hidden />
              </div>
              <h3 className="text-sm font-semibold text-slate-800 mb-1">No classes yet</h3>
              <p className="text-sm text-slate-500 max-w-xs mb-4">
                Browse teachers and enroll in a class to get started.
              </p>
              <Button asChild size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
                <Link to="/search">
                  <Search className="w-4 h-4" aria-hidden />
                  Find Teachers
                </Link>
              </Button>
            </div>
          )}

          {!isLoading && !isError && confirmedClasses.length > 0 && (
            <div className="space-y-4">
              {confirmedClasses.map((enrollment) => (
                <EnrollmentCard key={enrollment.offeringId} enrollment={enrollment} />
              ))}
            </div>
          )}

        </div>
      </main>
    </StudentLayout>
  );
}
